import { useState } from "react";
import axios from "axios";
import { Euro, Loader2, SearchCheck, Wrench, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const fmtEUR = (n) =>
  `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Pannello admin: audit dei prezzi listino nei piani di cura salvati.
 * Trova le opp assicurative salvate (per il bug storico del fallback)
 * con prezzi del listino BDD invece del listino assicurazione, mostra
 * l'anteprima e permette di correggere Mongo + GHL in un click.
 */
export default function ListinoPriceAuditPanel() {
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (dryRun) setRunning(true);
    else {
      const n = result?.opps_with_fixes || 0;
      if (!window.confirm(
        `Confermi la correzione di ${n} opportunità? Verranno aggiornati prezzi, totali e note anche su GHL. L'operazione non è reversibile.`
      )) return;
      setApplying(true);
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/listino-price-audit`,
        { dry_run: dryRun },
        { withCredentials: true, timeout: 600000 }
      );
      setResult(data);
      if (dryRun) {
        toast.info(
          data.opps_with_fixes > 0
            ? `Trovate ${data.opps_with_fixes} opportunità da correggere (${data.fixed_rows} prestazioni)`
            : "Nessun prezzo da correggere"
        );
      } else {
        toast.success(`Corrette ${data.opps_with_fixes} opportunità (${data.fixed_rows} prestazioni)`);
        if (data.ghl_failed > 0) toast.warning(`${data.ghl_failed} aggiornamenti GHL falliti — riprova`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'audit prezzi");
    } finally {
      setRunning(false);
      setApplying(false);
    }
  };

  const report = result?.report || [];
  const fixable = report.filter((e) => e.fixes?.length > 0);
  const issuesOnly = report.filter((e) => !e.fixes?.length && e.issues?.length > 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="listino-audit-panel">
      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
        <Euro className="w-4 h-4 text-amber-500" /> Audit prezzi listino
      </h3>
      <p className="text-[11px] text-slate-500 mt-1 mb-3">
        Trova i piani di cura assicurativi salvati con prezzi del listino BDD
        (bug storico del fallback) e li corregge col listino giusto, aggiornando
        anche valore e note su GHL.
      </p>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => run(true)}
          disabled={running || applying}
          className="rounded-xl text-xs h-9"
          data-testid="listino-audit-dry-run-btn"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SearchCheck className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Analizza</span>
        </Button>
        {result?.dry_run && (result.opps_with_fixes || 0) > 0 && (
          <Button
            type="button"
            onClick={() => run(false)}
            disabled={running || applying}
            className="rounded-xl text-xs h-9 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="listino-audit-apply-btn"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Correggi {result.opps_with_fixes} opp</span>
          </Button>
        )}
      </div>

      {result && (
        <div className="mt-3 text-xs" data-testid="listino-audit-result">
          <div className="text-slate-600 mb-2">
            {result.scanned} opp scansionate · {result.with_insurance} con assicurazione ·{" "}
            <span className={result.opps_with_fixes > 0 ? "font-bold text-amber-600" : "font-semibold text-emerald-600"}>
              {result.opps_with_fixes} da correggere
            </span>
            {!result.dry_run && result.opps_with_fixes > 0 && (
              <span className="ml-1 font-bold text-emerald-600">— CORRETTE ✓</span>
            )}
          </div>

          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {fixable.map((e) => (
              <div
                key={e.opportunity_id}
                className="border border-amber-200 bg-amber-50/50 rounded-lg p-2.5"
                data-testid={`listino-audit-opp-${e.opportunity_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-800 truncate">{e.contact_name || e.opportunity_id}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">{e.listino}</span>
                </div>
                <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                  {e.fixes.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-amber-700">{f.code}</span>
                      <span className="tabular-nums">
                        <span className="line-through text-rose-500">{fmtEUR(f.euro_from)}</span>
                        {" → "}
                        <span className="font-bold text-emerald-600">{fmtEUR(f.euro_to)}</span>
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t border-amber-200 pt-1 mt-1 font-bold">
                    <span>Totale</span>
                    <span className="tabular-nums">
                      <span className="line-through text-rose-500">{fmtEUR(e.old_total)}</span>
                      {" → "}
                      <span className="text-emerald-600">{fmtEUR(e.new_total)}</span>
                    </span>
                  </div>
                  {e.ghl_updated === false && (
                    <div className="text-rose-600 font-semibold">⚠ Aggiornamento GHL fallito</div>
                  )}
                  {e.issues?.length > 0 && (
                    <div className="text-slate-500">
                      {e.issues.length} righe non toccate (override manuale / codice assente)
                    </div>
                  )}
                </div>
              </div>
            ))}

            {issuesOnly.length > 0 && (
              <details className="text-[11px] text-slate-500">
                <summary className="cursor-pointer flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-slate-400" />
                  {issuesOnly.length} opp con sole anomalie (nessuna correzione automatica)
                </summary>
                <div className="mt-1 space-y-1 pl-4">
                  {issuesOnly.map((e) => (
                    <div key={e.opportunity_id}>
                      <span className="font-semibold">{e.contact_name}</span> ({e.listino}):{" "}
                      {e.issues.map((i) => `${i.code} ${i.type}`).join(", ")}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
